import React, { type FC, type PropsWithChildren } from 'react';

import { isBrowser } from '@lucid/utils';
import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Button from '../button/button';
import Heading from '../heading/heading';
import Image from '../image/image';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Link, { type IconAnimationType } from '../link/link';
import CtaWithSSO from '../cta/cta-with-sso/cta-with-sso';
import Text from '../text/text';
import VideoPlayer from '../video/video-player/video-player';
import MarkdownParser from '../markdown-parser/markdown-parser';

// TODO: can we extend Directus Hero_Section here?
export interface HeroProps {
  /** The background image to be placed behind the hero content. */
  backgroundImage?: Lucid.Directus.Generated.Directus_Files;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Array of additional custom params */
  customURLParams?: Lucid.URL.CustomParams[];
  /** The button CTA link and text. */
  cta?: Lucid.Directus.Generated.Links;
  /** date relating to text */
  date?: string;
  /** show the cta as a link instead of a button */
  willDisplayCtaAsLink?: boolean;
  /** Determines whether the page will add SSO buttons to CTA */
  hasSSO?: boolean;
  /** Adjusts text color for dark backgrounds */
  hasDarkBackground?: boolean;
  /** The heading text. */
  header: string;
  /** The header variants. */
  headerVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Icon animation type for cta links */
  iconAnimation?: IconAnimationType;
  /** An image to be placed next to the text. */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** Header and subheader left alignment and max-width set */
  isLeftAligned?: boolean;
  /** Scroll options for cta links */
  scrollToOptions?: ScrollIntoViewOptions;
  /** Information for JS ran jumplinks (Avoids issues withusing purely hrefs) */
  jumplinkData?: Lucid.Directus.Generated.Links;
  /** Checks if the Hero image should overflow with increased size. */
  shouldImageOverflow?: boolean;
  /** Determines if the stacked image should be hidden */
  shouldHideMobileImage?: boolean;
  /** Will use markdown parser in description */
  shouldUseMarkdown?: boolean;
  /** Checks if the Hero image should stack on smaller screens */
  shouldVideoStack?: boolean;
  /** Checks if the Hero image should stack on smaller screens. */
  shouldImageStack?: boolean;
  /** Should the media and content swap sides */
  shouldSwapPositions?: boolean;
  /** The subheader text displayed under the heading text. */
  subheader: string;
  /** The subheader text displayed under the heading text. */
  subheaderVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** The text displayed under the ctas */
  underCtaText?: string;
  /** The video to be placed next to the text */
  video?: string;
  /** The video accessible description. */
  videoDescription?: string;
  /** The unique ID for the description element (must be a valid id attribute) */
  videoDescriptionElementId?: string;
}

const Hero: FC<PropsWithChildren<HeroProps>> = ({
  backgroundImage,
  className,
  customURLParams,
  cta,
  date,
  willDisplayCtaAsLink,
  hasDarkBackground,
  hasSSO,
  header,
  headerVariant = 'hero3XL700',
  iconAnimation,
  image,
  isLeftAligned,
  scrollToOptions,
  jumplinkData,
  shouldHideMobileImage = false,
  shouldImageOverflow = false,
  shouldImageStack = false,
  shouldUseMarkdown = false,
  shouldVideoStack,
  shouldSwapPositions = false,
  subheader,
  subheaderVariant = 'paragraphL400',
  underCtaText,
  video,
  videoDescription,
  videoDescriptionElementId,
  ...rest
}) => {
  const hasImage = image && image.filename_disk;
  const hasVideo = !!video;
  const hasSideBySideVideo = hasVideo && !shouldVideoStack;

  const handleJumplinkOnClick = (e: React.SyntheticEvent<Element, Event>, jumplinkUrl: string) => {
    e.preventDefault();

    if (isBrowser()) {
      const el = document.getElementById(jumplinkUrl.slice(1));

      if (el) {
        el.scrollIntoView(scrollToOptions);
        // move keyboard focus for screenreaders
        el.tabIndex = -1;
        el.focus({ preventScroll: true });
      }
    }
  };

  return (
    <Box
      {...rest}
      css={theme => [
        {
          label: 'hero',
          padding: `${theme.space.s64}px 0 ${theme.space.s16}px`,
          [theme.breakpoints.bp900.min]: {
            padding: `${theme.space.s64}px 0`,
          },
        },
        hasSideBySideVideo && {
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          [theme.breakpoints.bp900.min]: {
            flexDirection: shouldSwapPositions ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
          },
        },
        hasImage && {
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          [theme.breakpoints.bp768.min]: {
            flexDirection: shouldSwapPositions ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
          },
        },
        hasImage &&
          shouldImageStack && {
            [theme.breakpoints.bp768.min]: {
              flexDirection: 'column',
            },
            [theme.breakpoints.bp1024.min]: {
              flexDirection: shouldSwapPositions ? 'row-reverse' : 'row',
            },
          },
      ]}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Box
        css={theme => [
          {
            paddingRight: 0,
          },
          hasSideBySideVideo && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            [theme.breakpoints.bp900.min]: {
              maxWidth: '580px',
              paddingRight: theme.space.s48,
              alignItems: 'start',
            },
          },
          hasImage && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            [theme.breakpoints.bp768.min]: {
              maxWidth: '440px',
              paddingRight: theme.space.s40,
              alignItems: 'start',
            },
          },
          hasImage &&
            shouldImageStack && {
              [theme.breakpoints.bp768.min]: {
                alignItems: 'center',
              },
              [theme.breakpoints.bp1024.min]: {
                alignItems: 'start',
              },
            },
        ]}
      >
        <Heading
          as="h1"
          css={theme => [
            {
              textAlign: 'center',
              width: '100%',
            },
            hasSideBySideVideo && {
              [theme.breakpoints.bp900.min]: {
                textAlign: 'left',
              },
            },
            hasImage && {
              [theme.breakpoints.bp768.min]: {
                textAlign: 'left',
              },
            },
            hasImage &&
              shouldImageStack && {
                [theme.breakpoints.bp768.min]: {
                  textAlign: 'center',
                },
                [theme.breakpoints.bp1024.min]: {
                  textAlign: 'left',
                },
              },
            hasDarkBackground && {
              color: theme.colors.white,
            },
            isLeftAligned && {
              textAlign: 'left',
              maxWidth: '980px',
            },
          ]}
          dataElementPageLocation="hero"
          variant={headerVariant}
        >
          {header}
        </Heading>
        {date && (
          <Text
            css={theme => [
              {
                width: '100%',
                textAlign: 'center',
              },
              hasImage && {
                [theme.breakpoints.bp768.min]: {
                  textAlign: 'left',
                },
              },
              hasImage &&
                shouldImageStack && {
                  [theme.breakpoints.bp768.min]: {
                    textAlign: 'center',
                  },
                  [theme.breakpoints.bp1024.min]: {
                    textAlign: 'left',
                  },
                },
              hasDarkBackground && {
                color: theme.colors.white,
              },
              isLeftAligned && {
                textAlign: 'left',
                maxWidth: '740px',
              },
            ]}
            data-testid="hero-date"
            dataElementPageLocation="hero"
            variant={subheaderVariant}
          >
            {date}
          </Text>
        )}
        {shouldUseMarkdown ? (
          <MarkdownParser>{subheader}</MarkdownParser>
        ) : (
          <Text
            css={theme => [
              {
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'pre-line',
              },
              hasSideBySideVideo && {
                [theme.breakpoints.bp900.min]: {
                  textAlign: 'left',
                },
              },
              hasImage && {
                [theme.breakpoints.bp768.min]: {
                  textAlign: 'left',
                },
              },
              hasImage &&
                shouldImageStack && {
                  [theme.breakpoints.bp768.min]: {
                    textAlign: 'center',
                  },
                  [theme.breakpoints.bp1024.min]: {
                    textAlign: 'left',
                  },
                },
              hasDarkBackground && {
                color: theme.colors.white,
              },
              isLeftAligned && {
                textAlign: 'left',
                maxWidth: '740px',
              },
            ]}
            dataElementPageLocation="hero"
            id="heroLinkDescription"
            variant={subheaderVariant}
          >
            {subheader}
          </Text>
        )}
        <Box
          css={theme => [
            {
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              [theme.breakpoints.bp768.min]: {
                width: 'initial',
              },
            },
            isLeftAligned && {
              justifyContent: 'flex-start',
            },
          ]}
        >
          {jumplinkData && jumplinkData?.final_url && (
            <Button onClick={event => handleJumplinkOnClick(event, jumplinkData.final_url!)}>
              {jumplinkData.default_link_text}
            </Button>
          )}
          {/* TODO: [Growth Team Test] - remove customURLParams prop when complete */}
          {hasSSO && (
            <CtaWithSSO customURLParams={customURLParams} linkText={cta?.default_link_text} linkUrl={cta?.final_url} />
          )}
          {cta && !hasSSO && (
            <Link
              aria-describedby="heroLinkDescription"
              displayAsButton={!willDisplayCtaAsLink}
              href={cta.final_url}
              iconAnimation={iconAnimation ? iconAnimation : 'navigational'}
              scrollToOptions={scrollToOptions}
              variant={hasDarkBackground ? 'secondary' : 'primary'}
            >
              {cta.default_link_text}
            </Link>
          )}
        </Box>
        {underCtaText && <Text css={theme => ({ margin: 0, color: theme.colors.coolGrey[80] })}>{underCtaText}</Text>}
      </Box>
      {hasVideo && (
        <Box
          css={theme => [
            hasSideBySideVideo && {
              width: '100%',
              maxWidth: '580px',
              marginTop: theme.space.s40,
              [theme.breakpoints.bp900.min]: {
                display: 'block',
                visibility: 'visible',
                marginTop: 0,
              },
            },
            shouldVideoStack && {
              width: '100%',
              maxWidth: '580px',
              margin: `${theme.space.s80}px auto 0 auto`,
              padding: `0 ${theme.space.s24}px`,
            },
          ]}
        >
          <VideoPlayer
            controls
            description={videoDescription}
            descriptionElementId={videoDescriptionElementId}
            url={video}
          />
        </Box>
      )}
      {hasImage && (
        <Box
          css={theme => [
            {
              display: 'none',
              visibility: 'hidden',
              width: '100%',
              maxWidth: '580px',
              marginTop: theme.space.s40,
              [theme.breakpoints.bp768.min]: {
                display: 'block',
                visibility: 'visible',
                marginTop: 0,
              },
            },
            !shouldHideMobileImage &&
              shouldImageStack && {
                display: 'block',
                visibility: 'visible',
              },
            shouldImageStack && {
              [theme.breakpoints.bp768.min]: {
                display: 'block',
                visibility: 'visible',
              },
              [theme.breakpoints.bp768.min]: {
                marginTop: theme.space.s40,
              },
              [theme.breakpoints.bp1024.min]: {
                marginTop: 0,
              },
            },
          ]}
        >
          <ImageWithPlaceholder
            alt={image?.translations?.[0]?.alt || ''}
            aspectRatio="4:3"
            css={[
              shouldImageOverflow && {
                overflow: 'unset',
                width: '150%',
                '& .image': {
                  height: '200%',
                  width: '200%',
                  top: '50%',
                  left: '-50%',
                  zIndex: -1,
                },
              },
            ]}
            dataElementPageLocation="hero"
            src={withImage(image)}
          />
        </Box>
      )}
      {backgroundImage && (
        <Image
          css={theme => ({
            height: '100%',
            maxHeight: '480px',
            objectFit: 'contain',
            position: 'absolute',
            right: 0,
            bottom: 0,
            zIndex: theme.zIndices.zNegative1,
            [theme.breakpoints.bp768.min]: {
              right: '10%',
              bottom: 'auto',
              top: 0,
            },
          })}
          dataElementPageLocation="hero"
          imageAlt=""
          imageSrc={withImage(backgroundImage)}
        />
      )}
    </Box>
  );
};

export { Hero };
export default Hero;
